/**
 * stay/match.ts — the Roamer Match Score™ (pure functions).
 *
 * Scores 0–100 with a full "Why This?" explanation. Honesty rules:
 *  • only components with real data contribute — unknowns are listed, not
 *    guessed, and the score is normalized over the components actually scored;
 *  • a listing with NO scorable data gets score null, never a number;
 *  • every point of the score is traceable to a reason.
 */

import type { HousingProfile, MatchReason, RoamerMatchScore, StayListing } from './models';

interface Component {
  weight: number;
  /** null ⇒ no data for this component (goes to unknowns). */
  evaluate(): { fraction: number; reason: string } | null;
  unknownLabel: string;
}

export function computeMatchScore(listing: StayListing, profile: HousingProfile): RoamerMatchScore {
  const components: Component[] = [
    {
      weight: 25,
      unknownLabel: 'rent unknown',
      evaluate: () => {
        if (listing.rentMonthly == null || profile.monthlyBudget == null) return null;
        const ratio = listing.rentMonthly / profile.monthlyBudget;
        if (ratio <= 1) {
          return {
            fraction: 1,
            reason: `Within budget (${listing.currency} ${listing.rentMonthly}/mo vs ${profile.monthlyBudget} budget)`,
          };
        }
        const fraction = Math.max(0, 1 - (ratio - 1) * 2); // 50% over budget ⇒ 0
        return {
          fraction,
          reason: `Over budget by ${Math.round((ratio - 1) * 100)}%`,
        };
      },
    },
    {
      weight: 20,
      unknownLabel: 'internet speed unknown',
      evaluate: () => {
        if (listing.internetMbps == null) return null;
        const min = profile.minInternetMbps ?? 25;
        const fraction = Math.min(1, listing.internetMbps / Math.max(min, 1));
        const verified = listing.internetVerified ? 'Verified' : 'Reported';
        return {
          fraction: listing.internetVerified ? fraction : fraction * 0.8, // unverified claims count less
          reason: `${verified} ${listing.internetMbps} Mbps internet`,
        };
      },
    },
    {
      weight: 15,
      unknownLabel: 'coworking distance unknown',
      evaluate: () => {
        if (listing.coworkingDistanceMin == null) return null;
        const fraction = Math.max(0, 1 - listing.coworkingDistanceMin / 30); // 30+ min ⇒ 0
        return { fraction, reason: `${listing.coworkingDistanceMin}-minute walk to coworking` };
      },
    },
    {
      weight: 10,
      unknownLabel: 'noise level unknown',
      evaluate: () => {
        if (listing.noiseLevel == null) return null;
        const map = { quiet: 1, moderate: 0.5, loud: 0 } as const;
        return { fraction: map[listing.noiseLevel], reason: `${listing.noiseLevel[0].toUpperCase()}${listing.noiseLevel.slice(1)} neighborhood` };
      },
    },
    {
      weight: 10,
      unknownLabel: 'no community ratings yet',
      evaluate: () => {
        if (listing.communityRating == null) return null;
        const recs = listing.communityRecommendations;
        const suffix = recs != null && recs > 0 ? ` — recommended by ${recs} WorkRoamer${recs === 1 ? '' : 's'}` : '';
        return { fraction: listing.communityRating / 5, reason: `Community rating ${listing.communityRating}/5${suffix}` };
      },
    },
    {
      weight: 10,
      unknownLabel: 'safety score unknown',
      evaluate: () => {
        if (listing.safetyScore == null) return null;
        const label = listing.safetyScore >= 80 ? 'Excellent' : listing.safetyScore >= 60 ? 'Good' : 'Modest';
        return { fraction: listing.safetyScore / 100, reason: `${label} safety score (${listing.safetyScore}/100)` };
      },
    },
    {
      weight: 5,
      unknownLabel: 'transit score unknown',
      evaluate: () => {
        if (listing.transitScore == null) return null;
        const label = listing.transitScore >= 70 ? 'Strong' : 'Limited';
        return { fraction: listing.transitScore / 100, reason: `${label} public transportation (${listing.transitScore}/100)` };
      },
    },
    {
      weight: 5,
      unknownLabel: 'workspace unknown',
      evaluate: () => {
        if (listing.amenities.workspace == null) return null;
        if (!profile.requiresWorkspace) {
          return listing.amenities.workspace
            ? { fraction: 1, reason: 'Dedicated workspace available' }
            : { fraction: 0.5, reason: 'No dedicated workspace' };
        }
        return listing.amenities.workspace
          ? { fraction: 1, reason: 'Has the workspace you require' }
          : { fraction: 0, reason: 'Missing your required workspace' };
      },
    },
  ];

  // Hard preference mismatches — evidence-based penalties, applied post-normalization.
  const penalties: MatchReason[] = [];
  if (profile.hasPets && listing.amenities.petFriendly === false) {
    penalties.push({ text: 'Not pet friendly (you travel with pets)', delta: -25 });
  }
  if (profile.preferredType != null && listing.type !== profile.preferredType) {
    penalties.push({ text: `Different housing type (${listing.type})`, delta: -5 });
  }
  const bonuses: MatchReason[] = [];
  if (listing.neighborhood != null && profile.preferredNeighborhoods.includes(listing.neighborhood)) {
    bonuses.push({ text: `In preferred neighborhood ${listing.neighborhood}`, delta: 5 });
  }
  if (listing.flexibleLease === true && profile.leaseLengthMonths == null) {
    bonuses.push({ text: 'Flexible lease terms', delta: 3 });
  }

  const reasons: MatchReason[] = [];
  const unknowns: string[] = [];
  let earned = 0;
  let possible = 0;
  for (const c of components) {
    const result = c.evaluate();
    if (result == null) {
      unknowns.push(c.unknownLabel);
      continue;
    }
    const points = result.fraction * c.weight;
    earned += points;
    possible += c.weight;
    reasons.push({ text: result.reason, delta: Math.round(points) });
  }

  if (possible === 0) {
    // Nothing scorable — an honest null beats a made-up number.
    return { listingId: listing.id, score: null, stars: null, reasons: [], unknowns };
  }

  let score = (earned / possible) * 100;
  for (const p of [...penalties, ...bonuses]) {
    score += p.delta;
    reasons.push(p);
  }
  score = Math.round(Math.min(100, Math.max(0, score)));

  return {
    listingId: listing.id,
    score,
    stars: Math.max(1, Math.min(5, Math.round(score / 20))),
    reasons,
    unknowns,
  };
}
